import { useDispatch } from 'react-redux';
import { useRef, useEffect } from 'react';
import { getBinListRequest, creditCardTransactionsStore } from 'Stores/CreditCardTransactions';
import { formatCardBinNumber } from 'Utils';
import { MasterPassCardType } from 'Models';
import { UserState, userStore } from 'Stores/User';
import { useSelector } from 'react-redux';

export const useBinListManager = (creditCardList?: MasterPassCardType[]) => {
  const dispatch = useDispatch();
  const userData: UserState = useSelector(userStore);
  const creditCardData = useSelector(creditCardTransactionsStore);

  const customerNumber = userData?.customerNumber;
  const sessionToken = userData?.sessionToken;
  const { isBinListRequesting, userTempCreditCard } = creditCardData;

  // Kullanıcıya özel önceki BIN listesi (kullanıcı değiştiğinde temizlenir)
  const previousBinListRef = useRef<string[]>([]);
  const currentCustomerRef = useRef<string | number | undefined>('');

  // creditCardList'ten BIN numaralarını çıkar (userTempCreditCard dahil)
  const extractBinNumbers = (cardList: MasterPassCardType[], tempCard?: any): string[] => {
    const allCards = [...(cardList || [])];

    // Geçici kart varsa ve creditCardList'te yoksa listeye ekle (duplicate önlemek için)
    if (tempCard?.maskedCardNumber) {
      const cardExists = cardList?.some((card) => card.maskedCardNumber?.slice(0, 6) === tempCard.maskedCardNumber?.slice(0, 6));
      if (!cardExists) {
        allCards.push(tempCard);
      }
    }

    const binNumbers = allCards
      // Servisin verdiği `cardBin` önceliklidir; yoksa maskeli numaraya düşülür.
      .map((card) => formatCardBinNumber(card.cardBin ?? card.maskedCardNumber))
      .filter((bin) => bin && bin.length === 8);

    return Array.from(new Set(binNumbers));
  };

  // Kullanıcı değiştiğinde önceki BIN listesini temizle
  useEffect(() => {
    if (customerNumber !== currentCustomerRef.current) {
      previousBinListRef.current = [];
      currentCustomerRef.current = customerNumber;
    }
  }, [customerNumber]);

  // creditCardList ve userTempCreditCard değiştiğinde BIN listesini güncelle
  useEffect(() => {
    if (!customerNumber || !sessionToken) return;
    if (isBinListRequesting) return;

    // creditCardList boşsa ama userTempCreditCard varsa yine de işlem yap
    const hasCards = (creditCardList && creditCardList.length > 0) || userTempCreditCard;
    if (!hasCards) return;

    // creditCardList varsa onu kullan, ama userTempCreditCard varsa ve creditCardList'te yoksa onu da dahil et
    // (extractBinNumbers duplicate kontrolü yapıyor)
    const cardsToUse = creditCardList && creditCardList.length > 0 ? creditCardList : [];
    const tempCardToUse = userTempCreditCard; // Her zaman userTempCreditCard'ı kontrol et, extractBinNumbers duplicate kontrolü yapacak

    const currentBinNumbers = extractBinNumbers(cardsToUse, tempCardToUse);

    if (currentBinNumbers.length === 0) return;

    // BIN listesi değişmişse istek at
    // Tüm BIN numaralarını gönder (önceki listede olanlar + yeniler)
    const hasChanged =
      currentBinNumbers.some((bin) => !previousBinListRef.current.includes(bin)) ||
      currentBinNumbers.length !== previousBinListRef.current.length;

    if (hasChanged) {
      // creditCardList varsa ve tüm kartların BIN numaraları önceki listede varsa istek atma
      // (İlk istekte userTempCreditCard ile sadece yeni kart gönderildi, ikinci istekte creditCardList güncellendiğinde duplicate önlemek için)
      if (creditCardList && creditCardList.length > 0 && !userTempCreditCard) {
        // creditCardList var ve userTempCreditCard yok (Masterpass'e kaydedilmiş durum)
        const creditCardListBinNumbers = extractBinNumbers(creditCardList, undefined);
        const allBinsAlreadyRequested = creditCardListBinNumbers.every((bin) =>
          previousBinListRef.current.includes(bin)
        );

        // Eğer creditCardList'teki tüm BIN numaraları önceki listede varsa ve sayılar aynıysa istek atma
        if (allBinsAlreadyRequested && creditCardListBinNumbers.length === previousBinListRef.current.length) {
          // previousBinListRef'i güncelle (creditCardList'teki tüm kartlar artık previousBinListRef'te olmalı)
          previousBinListRef.current = creditCardListBinNumbers;
          return; // İstek atma
        }

        // creditCardList varsa ve userTempCreditCard yoksa, creditCardListBinNumbers'ı kullan
        previousBinListRef.current = creditCardListBinNumbers;

        dispatch(
          getBinListRequest({
            CustomerNumber: customerNumber,
            SessionToken: sessionToken,
            BinNumbers: creditCardListBinNumbers,
          })
        );
        return;
      }

      // creditCardList yoksa veya userTempCreditCard varsa, currentBinNumbers'ı kullan
      // (currentBinNumbers zaten creditCardList + userTempCreditCard içeriyor)
      previousBinListRef.current = currentBinNumbers;

      dispatch(
        getBinListRequest({
          CustomerNumber: customerNumber,
          SessionToken: sessionToken,
          BinNumbers: currentBinNumbers,
        })
      );
    }
  }, [creditCardList, userTempCreditCard, customerNumber, sessionToken, isBinListRequesting, dispatch]);
};

export default useBinListManager;
